import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import { currentLocale } from '../../i18n'
import {
  getOrganizacionComercial, getArbolOrganizacionesComercial,
  archivarOrganizacionComercial, restaurarOrganizacionComercial, deleteOrganizacionComercial,
} from '../../api/client'
import { puede, mensajeError } from './comercialLogica'
import { urlSitio, caminoA, contarNodos } from './organizacionesLogica'
import { nombrePais } from './paises'
import { Seccion, Dato, Registro, MenuMas, AvisoFicha } from './ComercialFichaPartes'
import { quien } from './comercialUtiles'
import ComercialPanel from './ComercialPanel'
import ComercialRuta from './ComercialRuta'
import ComercialChip from './ComercialChip'
import ComercialOrganizacionForm from './ComercialOrganizacionForm'
import ComercialOrganizacionesArbol from './ComercialOrganizacionesArbol'
import ComercialContactosDeOrganizacion from './ComercialContactosDeOrganizacion'

// An organization's card in the side panel, in three modes (E5-2):
//   consulta -- tabs Datos, Contactos, Estructura and Registro, with its actions;
//   edición  -- "Editar" turns the same panel into the form;
//   creación -- id 'nueva' (?org=nueva, maybe &superior=<id>): the form at once.
// Saving goes back to consulta with the new data (a new one becomes its own
// card); cancelling goes back to the card, or leaves the panel when creating.
//   onCambio(): something was saved, archived, restored or deleted (the list reloads)
export default function ComercialFichaOrganizacion({ id, superior, abrir, reemplazar, atras, cerrar, onCambio }) {
  const { t } = useTranslation('comercial')
  const { user } = useAuth()
  const puedeEditar = puede(user, 'comercial_editar')
  const puedeEliminar = puede(user, 'comercial_configurar')
  const nueva = id === 'nueva'
  const [o, setO] = useState(null)
  const [error, setError] = useState('')
  const [editando, setEditando] = useState(false)
  const [pestana, setPestana] = useState('datos')
  const [aviso, setAviso] = useState(null)          // { texto, orgs?: [{ id, nombre_comercial }] } after an action
  const [ocupado, setOcupado] = useState(false)
  const salir = atras || cerrar

  useEffect(() => {
    if (nueva) return undefined
    let vivo = true
    getOrganizacionComercial(id).then(d => vivo && setO(d))
      .catch(e => vivo && setError(mensajeError(e, t, t('panel.cargaFallo'))))
    return () => { vivo = false }
  }, [id, nueva, t])

  // --- Creating and editing ---
  if (nueva || editando) {
    const titulo = nueva ? t('form.nueva') : t('form.editar')
    return (
      <ComercialPanel titulo={titulo} onCerrar={cerrar} onAtras={atras}
        encabezado={!nueva && o && <p className="text-xs text-gray-500 truncate">{o.ruta}</p>}>
        {nueva && !puedeEditar
          ? <p className="text-sm text-gray-500">{t('form.sinFuncion')}</p>
          : <ComercialOrganizacionForm org={nueva ? null : o} superiorInicial={nueva ? superior : null}
              onGuardado={ficha => {
                onCambio?.()
                if (nueva) reemplazar('org', ficha.id)
                else { setO(ficha); setEditando(false); setAviso(null) }
              }}
              onCancelar={() => (nueva ? salir() : setEditando(false))} />}
      </ComercialPanel>
    )
  }

  // --- Consulta ---
  const accion = async (llamar, alFallar) => {
    setOcupado(true)
    setAviso(null)
    try {
      setO(await llamar())
      onCambio?.()
    } catch (err) {
      setAviso(alFallar?.(err) || { texto: mensajeError(err, t, t('catalogo.fallo')) })
    } finally {
      setOcupado(false)
    }
  }
  const activas = o ? o.subordinadas.filter(s => !s.archivada) : []
  const archivar = () => {
    if (activas.length) { setAviso({ texto: t('ficha.archivarSubordinadas'), orgs: activas }); return }
    if (!window.confirm(t('ficha.archivarConfirm', { nombre: o.nombre_comercial }))) return
    accion(() => archivarOrganizacionComercial(o.id), err => {
      const d = err.response?.data?.detail
      return d?.code === 'tiene_subordinadas_activas' ? { texto: t('ficha.archivarSubordinadas'), orgs: d.subordinadas } : null
    })
  }
  const restaurar = () => accion(() => restaurarOrganizacionComercial(o.id), err => (
    err.response?.data?.detail?.code === 'superior_archivada' && o.superior
      ? { texto: t('ficha.restaurarSuperior'), orgs: [o.superior] } : null))
  const eliminar = async () => {
    if (!window.confirm(t('ficha.eliminarConfirm', { nombre: o.nombre_comercial }))) return
    setOcupado(true)
    setAviso(null)
    try {
      await deleteOrganizacionComercial(o.id)
      onCambio?.()
      salir()
    } catch (err) {
      const d = err.response?.data?.detail
      setAviso(d?.code === 'tiene_vinculos' ? { texto: t('ficha.eliminarConVinculos', { count: d.vinculos }) }
        : d?.code === 'tiene_subordinadas' ? { texto: t('errores.tiene_subordinadas'), orgs: d.subordinadas }
        : { texto: mensajeError(err, t, t('catalogo.fallo')) })
      setOcupado(false)
    }
  }

  const acciones = o && (puedeEditar || puedeEliminar) && (
    <>
      {puedeEditar && (
        <button type="button" onClick={() => { setAviso(null); setEditando(true) }} disabled={ocupado}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {t('ficha.editar')}
        </button>
      )}
      <MenuMas disabled={ocupado} opciones={[
        puedeEditar && (o.archivada
          ? { texto: t('ficha.restaurar'), onClick: restaurar }
          : { texto: t('ficha.archivar'), onClick: archivar }),
        puedeEliminar && {
          texto: t('ficha.eliminar'), onClick: eliminar, peligro: true,
          deshabilitada: o.subordinadas.length > 0 ? t('ficha.eliminarConSubordinadas') : null,
        },
      ].filter(Boolean)} />
    </>
  )

  return (
    <ComercialPanel titulo={o?.nombre_comercial || (error ? t('panel.ficha') : '')} cargando={!o && !error}
      onCerrar={cerrar} onAtras={atras} acciones={acciones} pestana={pestana} onPestana={setPestana}
      pestanas={[
        { id: 'datos', label: t('panel.datos') },
        { id: 'contactos', label: t('panel.contactos') },
        { id: 'estructura', label: t('panel.estructura') },
        { id: 'registro', label: t('panel.registro') },
      ]}
      encabezado={o && (
        <div className="space-y-1.5">
          <ComercialRuta ruta={[...o.ancestros, o]} onAbrir={x => abrir('org', x)} />
          <div className="flex flex-wrap gap-1">
            {o.tipos.map(x => <ComercialChip key={x.id} variante="tipo">{x.nombre}</ComercialChip>)}
            {o.archivada && (
              <ComercialChip variante="archivado" title={quien(t, o.archivada_por, o.archivada_en)}>{t('chips.archivada')}</ComercialChip>
            )}
          </div>
        </div>
      )}>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <AvisoFicha aviso={aviso} onCerrar={() => setAviso(null)} abrir={abrir} />
      {o && pestana === 'datos' && <Datos o={o} />}
      {o && pestana === 'contactos' && (
        <ComercialContactosDeOrganizacion org={o} abrir={abrir} puedeEditar={puedeEditar} onCambio={onCambio} />
      )}
      {o && pestana === 'estructura' && (
        <Estructura o={o} abrir={abrir} puedeEditar={puedeEditar} />
      )}
      {o && pestana === 'registro' && (
        <Registro r={o} etiquetaCreacion={t('ficha.creada')}
          archivo={{ si: o.archivada, por: o.archivada_por, en: o.archivada_en, etiqueta: t('chips.archivada') }} />
      )}
    </ComercialPanel>
  )
}

function Datos({ o }) {
  const { t } = useTranslation('comercial')
  const sitio = urlSitio(o.sitio_web)
  return (
    <div className="space-y-5">
      <Seccion titulo={t('ficha.identificacion')}>
        <Dato etiqueta={t('campos.nombre_comercial')}>{o.nombre_comercial}</Dato>
        <Dato etiqueta={t('campos.razon_social')}>{o.razon_social}</Dato>
        <Dato etiqueta={t('campos.identificador_tributario')}>{o.identificador_tributario}</Dato>
      </Seccion>
      <Seccion titulo={t('ficha.ubicacion')}>
        <Dato etiqueta={t('campos.pais')}>{o.pais && `${nombrePais(o.pais, currentLocale())} (${o.pais})`}</Dato>
        <Dato etiqueta={t('campos.ciudad')}>{o.ciudad}</Dato>
        <Dato etiqueta={t('campos.direccion')}>{o.direccion}</Dato>
        <Dato etiqueta={t('campos.sitio_web')}>
          {sitio && <a href={sitio} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:underline break-all">{o.sitio_web}</a>}
        </Dato>
      </Seccion>
      <Seccion titulo={t('campos.descripcion')}>
        {o.observaciones
          ? <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{o.observaciones}</p>
          : <p className="text-sm text-gray-300">—</p>}
      </Seccion>
    </div>
  )
}

// Only the organization's tree (its whole family, this one marked) with "+
// Suborganización"; the superior is changed in the edit form.
function Estructura({ o, abrir, puedeEditar }) {
  const { t } = useTranslation('comercial')
  const [arbol, setArbol] = useState(null)
  const [error, setError] = useState('')
  const raiz = o.ancestros[0]?.id ?? o.id
  useEffect(() => {
    let vivo = true
    // Its whole family, from the top: archived ones too (an archived card is in it), marked.
    getArbolOrganizacionesComercial({ raiz, archivadas: true }).then(d => vivo && setArbol(d))
      .catch(e => vivo && setError(mensajeError(e, t, t('panel.cargaFallo'))))
    return () => { vivo = false }
  }, [raiz, o, t])      // o: a new card after saving (a renamed or moved organization)
  const editable = puedeEditar && !o.archivada

  return (
    <div className="space-y-5">
      <Seccion titulo={t('ficha.familia')}>
        <div className="flex flex-wrap items-center justify-between gap-2 -mt-1">
          <p className="text-xs text-gray-500">
            {arbol && t('ficha.familiaResumen', { count: contarNodos(arbol), subordinadas: o.subordinadas.length })}
          </p>
          {editable && (
            <button type="button" onClick={() => abrir('org', 'nueva', { superior: o.id })}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
              + {t('ficha.nuevaSubordinada')}
            </button>
          )}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!arbol && !error && <p className="text-sm text-gray-400">{t('panel.cargando')}</p>}
        {arbol && (
          <div className="rounded-lg border border-gray-100 p-2">
            <ComercialOrganizacionesArbol key={o.id} nodos={arbol} abiertosIniciales={caminoA(arbol, o.id)}
              actual={o.id} onAbrir={x => abrir('org', x)} herramientas={contarNodos(arbol) > 8} />
          </div>
        )}
      </Seccion>
    </div>
  )
}
